import { QueryOptions, useMutation } from '@apollo/client';
import { FileAttachmentChange } from '@procureai/ui-modules-mui';
import { useCustomSnackbar } from '@procureai/ui-mui';

import { getFragmentData } from '@/generated/gql';
import {
  FileAttachmentFragment,
  TsrctAttachmentMutationAction,
  TsrctAttachmentParentVariant,
  TsrctAttachmentClassification,
} from '@/generated/gql/graphql';

import { FILE_ATTACHMENT_FRAGMENT, FILE_ATTACHMENTS_MUTATION } from './FileAttachmentsWidget.gql';
import { FileAttachmentStatus } from './FileAttachmentsWidget.types';

export enum FileAttachmentsWidgetEditFeature {
  UPLOAD = 'UPLOAD',
  CHANGE_VISIBILITY = 'CHANGE_VISIBILITY',
}

export const FILE_ACCEPTS = {
  'application/pdf': ['.pdf'],
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
  'application/msword': ['.doc'],
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
  'application/vnd.ms-excel': ['.xls'],
  'application/vnd.oasis.opendocument.text': ['.odt'],
  'application/vnd.oasis.opendocument.spreadsheet': ['.ods'],
  'application/vnd.openxmlformats-officedocument.presentationml.presentation': ['.pptx'],
  'application/vnd.ms-powerpoint': ['.ppt'],
  'application/vnd.oasis.opendocument.presentation': ['.odp'],
  'image/jpeg': ['.jpeg', '.jpg'],
  'image/png': ['.png'],
  'image/gif': ['.gif'],
  'image/bmp': ['.bmp'],
  'image/webp': ['.webp'],
  'image/tiff': ['.tiff', '.tif'],
  'image/svg+xml': ['.svg'],
};

export const classificationOptions = Object.values(TsrctAttachmentClassification);

type AttachmentFragmentRef = {
  ' $fragmentRefs'?:
    | {
        FileAttachmentFragment: FileAttachmentFragment;
      }
    | undefined;
};

/**
 * Extract the attachment fragments from a query result list
 */
export const getAttachmentFragments = (a: AttachmentFragmentRef[] | undefined) => {
  const attachments = a ? a.map((attachment) => getFragmentData(FILE_ATTACHMENT_FRAGMENT, attachment)) : [];
  return attachments.map((attachment) => ({
    ...attachment,
    classification: attachment.classification as TsrctAttachmentClassification,
  }));
};

/**
 * Groups attachments by their visibility parents.
 * Returns a map where the key is the visibility parent ID and the value is a set of attachment IDs visible to that parent.
 */
export const groupAttachmentsByVisibilityParents = (
  attachments: FileAttachmentFragment[],
): Map<string, Set<string>> => {
  const visibilitiesByParentId = new Map<string, Set<string>>();
  attachments.forEach((attachment) => {
    attachment.visibilities.forEach((visibility) => {
      if (!visibilitiesByParentId.has(visibility.id)) {
        visibilitiesByParentId.set(visibility.id, new Set());
      }
      visibilitiesByParentId.get(visibility.id)!.add(attachment.id);
    });
  });

  return visibilitiesByParentId;
};

/**
 * The status of an attachment based on its visibility to relevant parents.
 */
export const getStatusByParentVisibilities = (
  attachment: FileAttachmentFragment,
  visibilitiesByParentId: Map<string, Set<string>>,
  relevantParentIds: string[],
) => {
  if (relevantParentIds.length === 0) {
    return FileAttachmentStatus.HIDDEN;
  }

  const isVisibleToParent = (parentId: string) => visibilitiesByParentId.get(parentId)?.has(attachment.id) ?? false;

  if (relevantParentIds.every(isVisibleToParent)) {
    return FileAttachmentStatus.VISIBLE;
  }

  if (relevantParentIds.some(isVisibleToParent)) {
    return FileAttachmentStatus.PARTIALLY_VISIBLE;
  }

  return FileAttachmentStatus.HIDDEN;
};

interface UseFileAttachmentsUpdateMutationProps {
  parent: {
    id: string;
    variant: TsrctAttachmentParentVariant;
  };
  refetchQueries?: QueryOptions[];
}

export const useFileAttachmentsUpdateMutation = ({ refetchQueries, parent }: UseFileAttachmentsUpdateMutationProps) => {
  const { onError } = useCustomSnackbar();

  const [update, { loading: saving }] = useMutation(FILE_ATTACHMENTS_MUTATION, {
    refetchQueries,
    onError,
  });

  const save = (changes: FileAttachmentChange[]) => {
    update({
      variables: {
        data: {
          parent,
          attachments: changes.map((change) => {
            return {
              ...change,
              classification: change.classification
                ? TsrctAttachmentClassification[change.classification as keyof typeof TsrctAttachmentClassification]
                : undefined,
              action: TsrctAttachmentMutationAction[change.action],
            };
          }),
        },
      },
    });
  };

  return { save, saving };
};
